// ABOUTME: Image section component for the news article builder.
// ABOUTME: Shows full-width image with hover overlay for replace/delete actions.

import { Box, IconButton } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";

const ArticleImageSection = ({ image, onChange, onRemove, colors, theme }) => {
    const { t } = useTranslation();

    const handleUpload = useCallback(() => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;
            if (image?.previewUrl) {
                URL.revokeObjectURL(image.previewUrl);
            }
            const previewUrl = URL.createObjectURL(file);
            onChange?.({ ...(image?.imageId ? { imageId: image.imageId } : {}), file, previewUrl });
        };
        input.click();
    }, [image, onChange]);

    const handleRemoveImage = useCallback(() => {
        if (image?.previewUrl) {
            URL.revokeObjectURL(image.previewUrl);
        }
        onChange?.(null);
    }, [image, onChange]);

    const renderSrc = image?.url || image?.previewUrl;

    if (renderSrc) {
        return (
            <Box
                sx={{
                    position: "relative",
                    borderRadius: 3,
                    overflow: "hidden",
                    "&:hover .image-overlay": {
                        opacity: 1,
                    },
                }}
            >
                <Box
                    component="img"
                    src={renderSrc}
                    alt=""
                    sx={{
                        width: "100%",
                        aspectRatio: "16/9",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
                {/* Hover overlay with actions */}
                <Box
                    className="image-overlay"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0,0,0,0.3)",
                        opacity: 0,
                        transition: "opacity 150ms",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                    }}
                >
                    <IconButton
                        onClick={handleUpload}
                        sx={{
                            width: 40,
                            height: 40,
                            background: "rgba(255,255,255,0.9)",
                            color: colors.txt["primary"],
                            "&:hover": { background: "#fff" },
                        }}
                    >
                        <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                        onClick={onRemove}
                        sx={{
                            width: 40,
                            height: 40,
                            background: "rgba(255,255,255,0.9)",
                            color: colors.red[400],
                            "&:hover": { background: "#fff" },
                        }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            onClick={handleUpload}
            sx={{
                height: 160,
                borderRadius: 3,
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                cursor: "pointer",
                transition: "150ms",
                "&:hover": {
                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                },
            }}
        >
            <AddPhotoAlternateOutlinedIcon
                sx={{ fontSize: 32, color: colors.txt["secondary"] }}
            />
            <Box
                sx={{
                    fontSize: 13,
                    fontFamily: "Roboto, sans-serif",
                    color: colors.txt["secondary"],
                }}
            >
                {t("click_to_upload_image") || "Klik om een afbeelding te uploaden"}
            </Box>
        </Box>
    );
};

export default ArticleImageSection;
